/**
 * usePendingDomains - Hook para gerenciar domínios pendentes
 * 
 * Centraliza toda lógica de fetch, filtros, paginação e ações de domínios
 */
import { useState, useEffect, useRef } from 'react';
import {
    getPendingDomains,
    processPendingDomain,
    ignorePendingDomain,
    fetchDomainData,
    syncDomainsFromArticles,
    getPendingDomainsStats,
    getViews,
    getViewData,
    getAllViewCounts,
    importUrls,
    enrichDomain,
    enrichDomainsBatch
} from '../../services/api';

export const usePendingDomains = () => {
    // Estados de dados
    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [stats, setStats] = useState({ statusStats: [], priorityStats: [] });

    // Estados de views inteligentes
    const [views, setViews] = useState([]);
    const [viewCounts, setViewCounts] = useState({});
    const [selectedView, setSelectedView] = useState(null); // null = filtros manuais, string = view ativa

    // Estados de filtros
    const [statusFilter, setStatusFilter] = useState('all');
    const [showCompleted, setShowCompleted] = useState(true);
    const [priorityFilter, setPriorityFilter] = useState('');
    const [sortBy, setSortBy] = useState('firstSeen');
    const [sortDirection, setSortDirection] = useState('desc');
    const [processedAtFilter, setProcessedAtFilter] = useState('all');
    const [zeroedValuationFilter, setZeroedValuationFilter] = useState(false);
    const [enrichedByFilter, setEnrichedByFilter] = useState('all');
    const [audienceSourceFilter, setAudienceSourceFilter] = useState('all');
    const [advancedFilters, setAdvancedFilters] = useState({});

    // Filtro especial para IDs (ex: após importação)
    const [filteredIds, setFilteredIds] = useState([]);

    // Estados de busca com debounce
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const searchTimeoutRef = useRef(null);

    // Estados de paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalDomains, setTotalDomains] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(50);

    // Estados de sincronização
    const [syncLoading, setSyncLoading] = useState(false);
    const [syncResult, setSyncResult] = useState(null);

    /**
     * Buscar views disponíveis
     */
    const fetchViews = async () => {
        try {
            const response = await getViews();
            setViews(response.data.data.views || []);
        } catch (error) {
            console.error('Erro ao buscar views:', error);
        }
    };

    /**
     * Buscar contagens das views (badges)
     */
    const fetchViewCounts = async () => {
        try {
            const response = await getAllViewCounts();
            setViewCounts(response.data.data || {});
        } catch (error) {
            console.error('Erro ao buscar contagens:', error);
        }
    };

    /**
     * Mudar para uma view específica
     */
    const handleViewChange = (viewId) => {
        setSelectedView(viewId);
        setCurrentPage(1);
        // NÃO limpamos mais a busca ao trocar de view para manter o contexto do usuário
        if (viewId) {
            setStatusFilter('all');
            setPriorityFilter('');
            // Removido resete de busca
        }
    };

    /**
     * Fetch domínios com filtros aplicados OU usando view
     */
    const fetchDomains = async (page = currentPage) => {
        try {
            setLoading(true);
            let response;

            // Se uma view está selecionada, usar API de view
            if (selectedView) {
                const params = {
                    page,
                    limit: itemsPerPage,
                    search: searchTerm.trim()
                };
                response = await getViewData(selectedView, params);
            } else {
                // Usar API normal de pending domains com filtros
                const params = {
                    page,
                    limit: itemsPerPage,
                    showCompleted: showCompleted.toString(),
                    sortBy,
                    sortDirection,
                    processedAt: processedAtFilter !== 'all' ? processedAtFilter : undefined
                };

                if (filteredIds.length > 0) {
                    params.ids = filteredIds.join(',');
                }

                if (statusFilter && statusFilter !== 'all') {
                    params.status = statusFilter;
                }

                if (priorityFilter) {
                    params.priority = priorityFilter;
                }

                if (searchTerm && searchTerm.trim()) {
                    params.search = searchTerm.trim();
                }

                if (zeroedValuationFilter) {
                    params.zeroedValuation = 'true';
                }

                if (enrichedByFilter && enrichedByFilter !== 'all') {
                    params.enrichedBy = enrichedByFilter;
                }

                if (audienceSourceFilter && audienceSourceFilter !== 'all') {
                    params.audienceSource = audienceSourceFilter;
                }

                // Filtros avançados (metadados Gemini)
                if (advancedFilters.pais) params.pais = advancedFilters.pais;
                if (advancedFilters.estado) params.estado = advancedFilters.estado;
                if (advancedFilters.cidade) params.cidade = advancedFilters.cidade;
                if (advancedFilters.sede) params.sede = advancedFilters.sede;
                if (advancedFilters.abrangencia) params.abrangencia = advancedFilters.abrangencia;
                if (advancedFilters.tipo) params.tipo = advancedFilters.tipo;
                if (advancedFilters.categories) params.categories = advancedFilters.categories;

                // Filtro de dados faltando
                if (advancedFilters.missingData && advancedFilters.missingData.length > 0) {
                    params.missingData = advancedFilters.missingData.join(',');
                }

                response = await getPendingDomains(params);
            }

            setDomains(response.data.data);

            if (response.data.pagination) {
                setCurrentPage(response.data.pagination.page);
                setTotalPages(response.data.pagination.pages);
                setTotalDomains(response.data.pagination.total);
            }
            setError('');
        } catch (error) {
            console.error('Erro ao buscar domínios pendentes:', error);
            setError('Erro ao carregar domínios pendentes');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Buscar estatísticas
     */
    const fetchStats = async () => {
        try {
            const response = await getPendingDomainsStats();
            setStats(response.data.data);
        } catch (error) {
            console.error('Erro ao buscar estatísticas:', error);
        }
    };

    /**
     * Handler de busca com debounce
     */
    const handleSearchChange = (value) => {
        setSearchInput(value);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setSearchTerm(value);
        }, 500);
    };

    /**
     * Handler de mudança de página
     */
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            fetchDomains(newPage);
        }
    };

    /**
     * Handler de ordenação
     */
    const handleSort = (column) => {
        if (sortBy === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            const defaultDesc = ['firstSeen', 'processedAt', 'articleCount', 'priority', 'estimatedAudience'];
            setSortDirection(defaultDesc.includes(column) ? 'desc' : 'asc');
        }
    };

    /**
     * Processar domínio
     */
    const processDomain = async (domainId, processData) => {
        try {
            const response = await processPendingDomain(domainId, processData);

            if (!showCompleted) {
                setDomains(domains.filter(domain => domain._id !== domainId));
            } else {
                fetchDomains();
            }

            // Retornar articlesUpdated do backend
            const articlesUpdated = response.data?.articlesUpdated || 0;
            return { success: true, articlesUpdated };
        } catch (error) {
            console.error('Erro ao processar domínio:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Erro ao processar domínio';
            return { success: false, error: errorMessage };
        }
    };

    /**
     * Ignorar domínio
     */
    const ignoreDomain = async (domainId) => {
        try {
            await ignorePendingDomain(domainId, {
                reason: 'Domínio ignorado pelo usuário'
            });

            if (!showCompleted) {
                setDomains(domains.filter(d => d._id !== domainId));
            } else {
                fetchDomains();
            }
            return { success: true };
        } catch (error) {
            console.error('Erro ao ignorar domínio:', error);
            return { success: false, error: 'Erro ao ignorar domínio' };
        }
    };

    /**
     * Buscar dados de um domínio
     */
    const fetchDomain = async (domainId) => {
        try {
            await fetchDomainData(domainId);
            fetchDomains();
            return { success: true };
        } catch (error) {
            console.error('Erro ao buscar dados:', error);
            return { success: false, error: 'Erro ao buscar dados do domínio' };
        }
    };

    /**
     * Sincronizar domínios dos artigos
     */
    const syncDomains = async () => {
        try {
            setSyncLoading(true);
            setError('');
            const response = await syncDomainsFromArticles();
            setSyncResult(response.data.data);
            fetchDomains();
            fetchStats();
            return { success: true, data: response.data.data };
        } catch (error) {
            console.error('Erro na sincronização:', error);
            setError('Erro ao sincronizar domínios');
            return { success: false, error: 'Erro ao sincronizar domínios' };
        } finally {
            setSyncLoading(false);
        }
    };

    /**
     * Importar URLs em massa
     */
    const importUrlsBatch = async (urls) => {
        try {
            setLoading(true);
            const response = await importUrls(urls);
            // fetchDomains() removido pois setFilteredIds acionará o useEffect
            fetchStats();
            return { success: true, data: response.data.data };
        } catch (error) {
            console.error('Erro na importação:', error);
            return { success: false, message: error.response?.data?.message || 'Erro ao importar URLs' };
        } finally {
            setLoading(false);
        }
    };

    /**
     * Enriquecer domínio individual com Gemini API
     */
    const enrichSingleDomain = async (domainId) => {
        try {
            const response = await enrichDomain(domainId);
            // Atualizar o domínio na lista local
            setDomains(domains.map(d =>
                d._id === domainId
                    ? { ...d, ...response.data.data, enrichedAt: new Date() }
                    : d
            ));
            return { success: true, data: response.data.data };
        } catch (error) {
            console.error('Erro ao enriquecer domínio:', error);
            return {
                success: false,
                error: error.response?.data?.message || 'Erro ao enriquecer domínio'
            };
        }
    };

    /**
     * Enriquecer múltiplos domínios em lote
     */
    const enrichMultipleDomains = async (domainIds) => {
        try {
            const response = await enrichDomainsBatch(domainIds);
            // Recarregar lista após enriquecimento em lote
            fetchDomains();
            return { success: true, data: response.data.data };
        } catch (error) {
            console.error('Erro ao enriquecer domínios em lote:', error);
            return {
                success: false,
                error: error.response?.data?.message || 'Erro ao enriquecer domínios'
            };
        }
    };

    // Efeito para recarregar quando filtros ou view mudam
    useEffect(() => {
        setCurrentPage(1);
        fetchDomains(1);
        // Atualizar contagens quando view muda
        if (selectedView) {
            fetchViewCounts();
        }
        if (selectedView) {
            fetchViewCounts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, showCompleted, priorityFilter, processedAtFilter, zeroedValuationFilter, enrichedByFilter, audienceSourceFilter, advancedFilters, filteredIds, sortBy, sortDirection, itemsPerPage, searchTerm, selectedView]);

    // Cleanup do timeout ao desmontar
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    // Fetch inicial: stats, views e contagens
    useEffect(() => {
        fetchStats();
        fetchViews();
        fetchViewCounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        // Estados
        domains,
        loading,
        error,
        stats,

        // Views Inteligentes
        views,
        viewCounts,
        selectedView,
        handleViewChange,
        fetchViewCounts,

        // Filtros
        statusFilter,
        setStatusFilter,
        showCompleted,
        setShowCompleted,
        priorityFilter,
        setPriorityFilter,
        processedAtFilter,
        setProcessedAtFilter,
        zeroedValuationFilter,
        setZeroedValuationFilter,
        enrichedByFilter,
        setEnrichedByFilter,
        audienceSourceFilter,
        setAudienceSourceFilter,
        advancedFilters,
        setAdvancedFilters,
        filteredIds,
        setFilteredIds,
        sortBy,
        sortDirection,
        searchInput,

        // Paginação
        currentPage,
        totalPages,
        totalDomains,
        itemsPerPage,
        setItemsPerPage,

        // Sync
        syncLoading,
        syncResult,

        // Ações
        fetchDomains,
        fetchStats,
        handleSearchChange,
        handlePageChange,
        handleSort,
        processDomain,
        ignoreDomain,
        fetchDomain,
        syncDomains,
        importUrlsBatch,
        enrichSingleDomain,
        enrichMultipleDomains
    };
};
